import React, { FC, useEffect, ReactNode } from 'react'
import { Button, Header } from '@lightspeed/design-system-react'
import { VendImage } from '@vend/react-utilities'
import VendNumber from 'vend-number'
import {
  ApiRegister,
  ApiRetailerPaymentType,
  ApiTransaction,
} from '@vend/types'
import { LSPAYModalContent } from '../modal-states/LSPAYModalContent'
import { ErrorContent } from '../modal-states/ErrorContent'
import {
  useCleanExitOnUnmount,
  useCleanExitOnWindowClose,
} from '../../hooks/exitHooks'
import { usePaymentEffectReducer } from '../../hooks/use-payment-effect-reducer/usePaymentEffectReducer'
import { isRetryable } from '../../hooks/use-payment-effect-reducer/reducer'
import { ConnectingToTerminal } from '../modal-states/ConnectingToTerminal'
import { CheckingPairing } from '../modal-states/CheckingPairing'
import { Processing } from '../modal-states/Processing'
import { SwiperError } from '../../helpers/lspay.types'

interface Props {
  onError: () => void
  onSuccess: (transaction: Promise<ApiTransaction>) => void
  transactionId: string
  amount: VendNumber
  outletId: string
  currency: string
  register: ApiRegister
  registerSaleId: string
  retailerPaymentType: ApiRetailerPaymentType
  setDismissible: (dismissible: boolean) => void
  setDisableCNP: (disableCNP: boolean) => void
  setIntentClientSecret: (intentClientSecret: string) => void
  setSwiperError: (swiperError: SwiperError) => void
  shouldUseSimulator?: boolean
}

const LSPAYPaymentTerminal: FC<Props> = ({
  onError,
  onSuccess,
  amount,
  outletId,
  currency,
  retailerPaymentType,
  register,
  transactionId,
  registerSaleId,
  setDismissible,
  setDisableCNP,
  setIntentClientSecret,
  setSwiperError,
  shouldUseSimulator,
}) => {
  // This effect reducer controls the state changes and side-effects
  const [paymentState, dispatch] = usePaymentEffectReducer({
    outletId,
    register,
    retailerPaymentType,
    amount,
    currency,
    transactionId,
    registerSaleId,
    setDisableCNP,
    setIntentClientSecret,
    setSwiperError,
    shouldUseSimulator,
  })

  // When we're done, call the onSuccess callback
  useEffect(() => {
    if (paymentState.status === 'done') {
      onSuccess(Promise.resolve(paymentState.transaction))
    }
  }, [onSuccess, paymentState])

  // If the modal is in an error state set the modal to be dismissible
  // otherwise make it non-dismissible
  useEffect(() => {
    if (paymentState.status === 'error') {
      setDismissible(true)
      return
    }

    setDismissible(false)
  }, [paymentState, setDismissible])

  // Cleanly exit when the user closes/refreshes the browser window or React unmounts this component
  const terminal =
    'terminal' in paymentState ? paymentState.terminal : undefined
  useCleanExitOnWindowClose(terminal)
  useCleanExitOnUnmount(terminal)

  const getPaymentContent = (): ReactNode => {
    switch (paymentState.status) {
      case 'fetchingPairing': {
        return <CheckingPairing headingType="sub-heading" />
      }
      case 'connectingToTerminal':
      case 'creatingIntent': {
        return <ConnectingToTerminal headingType="sub-heading" />
      }
      case 'collecting': {
        return (
          <LSPAYModalContent
            actions={
              <Button
                variant="no"
                className="vd-mr2"
                onClick={() =>
                  paymentState.terminal
                    .cancelCollectPaymentMethod()
                    .catch(() => {
                      // absorb this error, there's nothing to do about it
                    })
                }
                data-track="cancel-payment"
              >
                Cancel Payment
              </Button>
            }
          >
            <VendImage
              relativeImagePath="payments/insert-card-v1.svg"
              relativeDarkImagePath="payments/insert-card-darkmode-v1.svg"
              className="vd-mb2"
            />
            <Header type="sub-heading" level="2">
              Tap, insert or swipe card.
            </Header>
          </LSPAYModalContent>
        )
      }
      // TODO https://vendhq.atlassian.net/browse/LR-3530
      // implement view while captured payment is being persisted
      case 'capturing': {
        return <Processing isRefund={false} />
      }
      case 'done': {
        // Modal should be closed if the state was done.
        // Hence we can just return
        return
      }
      case 'error': {
        return (
          <ErrorContent
            headingType="sub-heading"
            lspayError={paymentState.errorMessage}
            disableRetry={!isRetryable(paymentState.previousState.status)}
            retry={() =>
              dispatch({
                type: 'RETRY',
                previousState: paymentState.previousState,
              })
            }
            close={() => {
              // notify the parent of the error
              onError()
            }}
          />
        )
      }
      default: {
        // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
        const _exhaustiveCheck: never = paymentState
        return _exhaustiveCheck
      }
    }
  }

  return <>{getPaymentContent()}</>
}
export { LSPAYPaymentTerminal }
